<template>
  <AccountLayout active-menu="shipping">
    <div class="shipping-page">
      <h1>Shipping Cost Calculator</h1>
      <p class="intro">
        Users enter destination country and package weight, the system will estimate the approximate shipping cost based
        on
        preset freight rates.
      </p>
      <form class="shipping-form" @submit.prevent="calculateShipping">
        <label><b>*</b>Destination Country</label>
        <div class="select-box">
          <el-select v-model="form.country" class="country-select" popper-class="shipping-country-popper" filterable
            remote reserve-keyword clearable placeholder="Please select a country" :remote-method="remoteCountrySearch"
            :loading="countryLoading" @change="onCountryChange" @clear="onCountryClear">
            <el-option v-for="c in countryOptions" :key="c.country_code" :label="c.country_name"
              :value="c.country_code">
              <span class="country-flag">{{ c.flag }}</span>
              <span class="country-name">{{ c.country_name }}</span>
            </el-option>
          </el-select>
        </div>

        <label><b>*</b>City Name <span>(English)</span></label>
        <el-input v-model="form.city_name" placeholder="Enter destination city name in English" class="form-input" />

        <!-- <label>Postal Code <span v-if="needsPostalCode" class="req-mark"><b>*</b>required</span><span v-else
            class="opt-mark">(optional for this country)</span></label>
        <el-input v-model="form.postal_code" :placeholder="postalPlaceholder" class="form-input" /> -->

        <label><b>*</b>Package Weight (KG)</label>
        <el-input v-model="form.weight" placeholder="Enter total weight of spare parts" class="form-input" />

        <label>Dimensions <span>(cm, optional)</span></label>
        <div class="dimensions">
          <el-input v-model="form.length" placeholder="Length" class="dim-input" />
          <em>X</em>
          <el-input v-model="form.width" placeholder="Width" class="dim-input" />
          <em>X</em>
          <el-input v-model="form.height" placeholder="Height" class="dim-input" />
        </div>

        <button type="submit" :disabled="loading">{{ loading ? 'Calculating...' : 'Calculate Estimated Shipping Fee'
        }}</button>
      </form>

      <div v-if="showResult" class="result-box">
        <h2>Estimated Shipping Cost</h2>
        <div v-if="products.length" class="products">
          <div v-for="p in products" :key="p.product_code" class="product-item">
            <div class="product-head">
              <span class="product-name">{{ p.product_name }}</span>
              <strong class="product-price">
                USD {{ p.usd_total_price }}
              </strong>
            </div>
            <div class="product-meta">
              <span v-if="p.weight != null">Weight: {{ p.weight }} {{ p.weight_unit === 'metric' ? 'kg' : 'lb' }}</span>
              <span v-if="p.total_transit_days != null">Transit: {{ p.total_transit_days }} day(s)</span>
              <span v-if="p.delivery_date">Delivery: {{ formatDateTime(p.delivery_date) }}</span>
              <!-- <span v-if="p.usd_total_price != null" class="usd-total">USD Total: ${{ p.usd_total_price }}</span> -->
              <span v-if="p.display.type_name" class="price-type-tag">{{ p.display.type_name }}</span>
            </div>
            <ul v-if="p.display.breakdown && p.display.breakdown.length" class="breakdown">
              <li v-for="b in p.display.breakdown" :key="b.name + (b.service_code || '')">
                <span>{{ b.name }}</span>
                <span> USD {{ b.usd_price }}</span>
              </li>
            </ul>
          </div>
        </div>
        <p v-else class="empty-tip">No shipping options returned for the given input.</p>
        <p class="note">{{ note }}</p>
      </div>
    </div>
  </AccountLayout>
</template>

<script>
import AccountLayout from '@/components/common/AccountLayout.vue'

export default {
  name: 'ShippingCalculatorPage',
  components: {
    AccountLayout
  },
  data() {
    return {
      form: {
        country: '',
        city_name: '',
        // postal_code: '',
        weight: '',
        length: '',
        width: '',
        height: ''
      },
      countries: [],
      selectedCountry: null,
      countryLoading: false,
      countryTimer: null,
      products: [],
      note: '',
      showResult: false,
      loading: false
    }
  },
  computed: {
    needsPostalCode() {
      return !!(this.selectedCountry && this.selectedCountry.has_postal_code === 1)
    },
    postalPlaceholder() {
      if (!this.selectedCountry) return 'Select a country first'
      return this.needsPostalCode
        ? 'Enter postal code (required by DHL)'
        : 'Optional - this country has no postal code'
    },
    countryOptions() {
      if (
        this.selectedCountry &&
        !this.countries.find(c => c.country_code === this.selectedCountry.country_code)
      ) {
        return [this.selectedCountry, ...this.countries]
      }
      return this.countries
    }
  },
  mounted() {
    this.loadCountries()
  },
  methods: {
    async loadCountries(keyword = '') {
      this.countryLoading = true
      try {
        const res = await this.$api({
          url: 'Shipping/dhlCountries',
          method: 'get',
          data: {
            page: 1,
            page_size: 50,
            ...(keyword ? { keyword } : {})
          }
        })
        const items = (res && res.data && res.data.items) || []
        this.countries = items
      } catch (e) {
        this.countries = []
      } finally {
        this.countryLoading = false
      }
    },
    remoteCountrySearch(query) {
      if (this.countryTimer) clearTimeout(this.countryTimer)
      const keyword = (query || '').trim()
      this.countryTimer = setTimeout(() => {
        this.loadCountries(keyword)
      }, 300)
    },
    onCountryChange(code) {
      if (!code) {
        this.selectedCountry = null
        return
      }
      const found = this.countries.find(c => c.country_code === code)
      if (found) {
        this.selectedCountry = found
        // if (found.has_postal_code !== 1) {
        //   this.form.postal_code = ''
        // }
      }
    },
    onCountryClear() {
      this.selectedCountry = null
      // this.form.postal_code = ''
      this.loadCountries()
    },
    formatDateTime(str) {
      if (!str) return ''
      const dateStr = String(str).replace('T', ' ')
      return dateStr.length > 16 ? dateStr.slice(0, 16) : dateStr
    },
    resolvePrice(p) {
      const prices = Array.isArray(p && p.prices) ? p.prices : []
      const basec = prices.find(item => item.type == 'BASEC')
      if (basec) {
        return {
          currency: basec.currency,
          price: basec.price,
          breakdown: Array.isArray(p.breakdown) ? p.breakdown : [],
          type_name: basec.type_name || 'Base Currency'
        }
      }
      const billc = prices.find(item => item.type == 'BILLC')
      if (billc) {
        return {
          currency: billc.currency,
          price: billc.price,
          breakdown: Array.isArray(p.breakdown) ? p.breakdown : [],
          type_name: billc.type_name || 'Billing Currency'
        }
      }
      return {
        currency: p.currency,
        price: p.total_price,
        breakdown: Array.isArray(p.breakdown) ? p.breakdown : [],
        type_name: ''
      }
    },
    async calculateShipping() {
      if (!this.form.country) {
        this.$message.error('Please select a destination country')
        return
      }
      if (!this.form.city_name || !this.form.city_name.trim()) {
        this.$message.error('Please enter the destination city name')
        return
      }
      // if (this.needsPostalCode && !this.form.postal_code) {
      //   this.$message.error('Please enter the postal code for the selected country')
      //   return
      // }
      if (!this.form.weight) {
        this.$message.error('Please enter the package weight')
        return
      }
      this.loading = true
      try {
        const res = await this.$api({
          url: 'Shipping/dhlQuote',
          method: 'post',
          data: {
            country_code: this.form.country,
            city_name: this.form.city_name.trim(),
            // postal_code: this.form.postal_code || '',
            weight: this.form.weight,
            length: this.form.length || '',
            width: this.form.width || '',
            height: this.form.height || '',
            // declared_value: 100,
            // is_customs_declarable: 1
          }
        })
        if (!res || res.code !== 200) {
          const msg = (res && res.message) || 'Failed to calculate shipping cost'
          this.$message.error(msg)
          this.showResult = false
          return
        } else {
          this.$message.success('Shipping cost calculated successfully')
        }
        const data = (res && res.data) || {}
        this.products = (Array.isArray(data.products) ? data.products : []).map(p => ({
          ...p,
          display: this.resolvePrice(p)
        }))
        this.note = data.note || 'This price is for reference only, the final freight will be confirmed after warehouse weighing.'
        this.showResult = true
      } catch (e) {
        const msg = (e && e.message) || 'Failed to calculate shipping cost'
        this.$message.error(msg)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.shipping-page h1 {
  margin: 0 0 34px;
  color: #111;
  font-size: 26px;
  font-weight: 400;
}

.intro {
  max-width: 1100px;
  margin: 0 0 42px;
  color: #999;
  font-size: 22px;
  line-height: 1.35;
}

.shipping-form {
  width: 676px;
}

.shipping-form label {
  display: block;
  margin: 0 0 14px;
  color: #111;
  font-size: 16px;
  line-height: 1.25;
}

.shipping-form label:not(:first-child) {
  margin-top: 44px;
}

.shipping-form b {
  color: #F34B01;
}

.shipping-form label span {
  margin-left: 18px;
}

.shipping-form .form-input,
.shipping-form .dim-input {
  width: 100%;
}

.shipping-form .form-input ::v-deep .el-input__inner,
.shipping-form .dim-input ::v-deep .el-input__inner,
.select-box .country-select {
  width: 100%;
  height: 48px;
  border: 1px solid #ddd;
  border-radius: 7px;
  color: #333;
  font-size: 16px;
  outline: none;
  background: #fff;
}

.shipping-form .form-input ::v-deep .el-input__inner::placeholder,
.shipping-form .dim-input ::v-deep .el-input__inner::placeholder,
.select-box .country-select ::v-deep .el-input__inner::placeholder {
  color: #999;
}

.select-box .country-select ::v-deep .el-input__inner {
  height: 48px;
  border: 1px solid #ddd;
  border-radius: 7px;
  padding: 0 40px 0 20px;
  color: #333;
  font-size: 16px;
  background: #fff;
}

.select-box .country-select ::v-deep .el-input .el-input__icon {
  color: #999;
  line-height: 48px;
}

.country-flag {
  margin-right: 8px;
}

.dimensions {
  display: grid;
  grid-template-columns: 180px 42px 180px 42px 180px;
  align-items: center;
}

.dimensions em {
  color: #111;
  font-style: normal;
  text-align: center;
  font-size: 20px;
}

.shipping-form button {
  width: 100%;
  height: 45px;
  margin-top: 43px;
  border: 0;
  border-radius: 4px;
  background: #F34B01;
  color: #fff;
  cursor: pointer;
  font-size: 18px;
}

.result-box {
  width: 676px;
  min-height: 195px;
  margin-top: 18px;
  padding: 28px 32px;
  border-radius: 4px;
  background: #fff1ed;
  box-sizing: border-box;
}

.result-box h2 {
  margin: 0 0 20px;
  color: #F34B01;
  font-size: 22px;
  font-weight: 400;
}

.result-box .products {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.result-box .product-item {
  padding: 16px 18px;
  border-radius: 4px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(243, 75, 1, 0.08);
}

.result-box .product-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
}

.result-box .product-name {
  color: #111;
  font-size: 18px;
  font-weight: 600;
}

.result-box .product-price {
  color: #F34B01;
  font-size: 22px;
  font-weight: 700;
  white-space: nowrap;
}

.result-box .product-meta {
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  color: #666;
  font-size: 13px;
}

.result-box .breakdown {
  margin: 10px 0 0;
  padding: 0;
  list-style: none;
  border-top: 1px dashed #f0d9cc;
}

.result-box .breakdown li {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  color: #999;
  font-size: 13px;
}

.result-box .price-type-tag {
  margin-left: auto;
  padding: 1px 8px;
  border-radius: 3px;
  background: #ffe2d4;
  color: #F34B01;
  font-size: 12px;
}

.result-box .usd-total {
  font-weight: 600;
  color: #111;
}

.result-box .empty-tip {
  margin: 0;
  color: #999;
  font-size: 14px;
}

.result-box .note {
  margin: 22px 0 0;
  color: #666;
  font-size: 14px;
  line-height: 1.45;
}

.shipping-form .req-mark b {
  color: #F34B01;
}

.shipping-form .req-mark,
.shipping-form .opt-mark {
  margin-left: 18px;
  font-size: 13px;
  color: #999;
}

.shipping-form button:disabled {
  background: #f0a98a;
  cursor: not-allowed;
}

@media (max-width: 780px) {

  .shipping-form,
  .result-box {
    width: 100%;
  }

  .dimensions {
    grid-template-columns: 1fr 28px 1fr 28px 1fr;
  }

  .result-box .note {
    width: auto;
  }
}
</style>

<style>
.shipping-country-popper .el-select-dropdown__item.selected {
  color: #F34B01;
  font-weight: 700;
}
</style>
